import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Card, Button, Form, Row, Col } from 'react-bootstrap';
import Breadcrumbs from '../../Common/Breadcrumbs';
import TournamentTeamRegistrationsTable from '../../Common/TournamentTeamRegistrationsTable';
import Select from 'react-select';
import ShimmerLoader from '../../Common/ShimmerLoader';
import { DeleteModal } from '../../Common/DeleteModal';
import { 
  getTournamentTeamRegistrations,
  deleteTournament,
  TournamentTeamRegistration
} from '../../api/tournament/registeredTournamentsService';
import { hasAnyPermission, hasPermission, type User } from '../../utils/roleBasedAccess';
import './RegisteredTournaments.css';

interface Tournament {
  _id: string;
  name: string;
  shortName: string;
  logo: string;
  location: string;
  startDate: string;
  endDate: string;
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  sports: string;
  ageCategories: string[];
  isSelected?: boolean;
}

const RegisteredTournaments = () => {
  const navigate = useNavigate();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  
  // State management
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sportsFilter, setSportsFilter] = useState('all');
  const [venueFilter, setVenueFilter] = useState('all');
  const [ageCategoryFilter, setAgeCategoryFilter] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isPageLoading, setIsPageLoading] = useState(false);
  
  // Dynamic filter options
  const [dynamicSportsOptions, setDynamicSportsOptions] = useState<Array<{value: string, label: string}>>([]);
  const [dynamicVenueOptions, setDynamicVenueOptions] = useState<Array<{value: string, label: string}>>([]);
  const [dynamicAgeCategoryOptions, setDynamicAgeCategoryOptions] = useState<Array<{value: string, label: string}>>([]);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tournamentToDelete, setTournamentToDelete] = useState<Tournament | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Check if user has access
  const userData: User = {
    ...(user || {}),
    subRole: role,
    permissions: permissions,
  };

  const isAdminUser = hasAnyPermission(userData, [
    'tournament.admin',
    'tournament.manage',
    'tournament.view',
    'tournament.read',
    'tournament.registrations.view',
    'tournament.registrations.manage',
    'tournament.registration_details.manage',
  ]);

  useEffect(() => {
    if (!isAdminUser) {
      setLoading(false);
      return;
    }
    loadData();
  }, [isAdminUser]);

  const loadData = async () => {
    try {
      setLoading(true);
      await loadAllTournaments(1);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllTournaments = async (page: number = 1) => {
    try {
      setIsPageLoading(true);
      
      const response = await getTournamentTeamRegistrations(page);
      
      if (response.statusCode === 200 && response.status === 'success') {
        const tournamentData = response.data.paginatedResults.map((tournament: TournamentTeamRegistration) => ({
          _id: tournament._id,
          name: tournament.name,
          shortName: tournament.shortName,
          logo: tournament.logo,
          location: tournament.location,
          startDate: tournament.startDate,
          endDate: tournament.endDate,
          status: tournament.status,
          sports: tournament.sports,
          ageCategories: tournament.ageCategories,
          isSelected: false
        }));
        
        setTournaments(tournamentData);
        setTotalCount(response.data.totalCount);
        setTotalPages(Math.ceil(response.data.totalCount / 10)); // Assuming 10 items per page
        
        // Update dynamic filter options
        updateDynamicFilterOptions(tournamentData);
        
      } else {
        console.error('❌ Invalid response format:', response);
        throw new Error('Invalid response format from API');
      }
    } catch (error: any) {
      console.error('❌ Error loading tournament team registrations:', error);
      throw error;
    } finally {
      setIsPageLoading(false);
    }
  };

  const updateDynamicFilterOptions = (tournamentData: Tournament[]) => {
    // Extract unique sports
    const uniqueSports = Array.from(new Set(tournamentData.map(t => t.sports))).filter(Boolean);
    const sportsOptions = [
      { value: 'all', label: 'All' },
      ...uniqueSports.map(sport => ({ value: sport, label: sport }))
    ];
    setDynamicSportsOptions(sportsOptions);

    // Extract unique venues (locations)
    const uniqueVenues = Array.from(new Set(tournamentData.map(t => t.location))).filter(Boolean);
    const venueOptions = [
      { value: 'all', label: 'All' },
      ...uniqueVenues.map(venue => ({ value: venue, label: venue }))
    ];
    setDynamicVenueOptions(venueOptions);

    // Extract unique age categories
    const allAgeCategories = tournamentData.flatMap(t => t.ageCategories);
    const uniqueAgeCategories = Array.from(new Set(allAgeCategories)).filter(Boolean);
    const ageCategoryOptions = [
      { value: 'all', label: 'All' },
      ...uniqueAgeCategories.map(category => ({ value: category, label: category }))
    ];
    setDynamicAgeCategoryOptions(ageCategoryOptions);
  };

  // Sports options - use dynamic data if available, otherwise fallback to static
  const sportsOptions = dynamicSportsOptions.length > 0 ? dynamicSportsOptions : [
    { value: 'all', label: 'All' },
    { value: 'Football', label: 'Football' },
    { value: 'Tennis', label: 'Tennis' },
    { value: 'Pickleball', label: 'Pickleball' }
  ];

  // Venue options - use dynamic data if available, otherwise fallback to static
  const venueOptions = dynamicVenueOptions.length > 0 ? dynamicVenueOptions : [
    { value: 'all', label: 'All' },
    { value: 'Mumbai', label: 'Mumbai' },
    { value: 'Pune', label: 'Pune' },
    { value: 'Bengaluru', label: 'Bengaluru' }
  ];

  // Age Category options - use dynamic data if available, otherwise fallback to static
  const ageCategoryOptions = dynamicAgeCategoryOptions.length > 0 ? dynamicAgeCategoryOptions : [
    { value: 'all', label: 'All' },
    { value: 'U8', label: 'U8' },
    { value: 'U10', label: 'U10' },
    { value: 'U12', label: 'U12' },
    { value: 'U14', label: 'U14' },
    { value: 'U16', label: 'U16' },
    { value: 'U18', label: 'U18' }
  ];

  // Filter tournaments based on search and filters
  const filteredTournaments = tournaments.filter(tournament => {
    const matchesSearch = tournament.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tournament.shortName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tournament.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSports = sportsFilter === 'all' || tournament.sports === sportsFilter;
    const matchesVenue = venueFilter === 'all' || tournament.location === venueFilter;
    const matchesAgeCategory = ageCategoryFilter === 'all' || 
                              tournament.ageCategories.includes(ageCategoryFilter);
    
    return matchesSearch && matchesSports && matchesVenue && matchesAgeCategory;
  });

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadAllTournaments(page);
  };



  // Handle filter changes
  const handleSportsFilterChange = (selectedOption: any) => {
    setSportsFilter(selectedOption.value);
  };

  const handleVenueFilterChange = (selectedOption: any) => {
    setVenueFilter(selectedOption.value);
  };

  const handleAgeCategoryFilterChange = (selectedOption: any) => {
    setAgeCategoryFilter(selectedOption.value);
  };

  // Handle tournament actions
  const handleViewTournament = (tournament: Tournament) => {
    navigate(`/registered-tournaments/${tournament._id}`);
  };

  const handleEditTournament = (tournament: Tournament) => {
    navigate(`/registered-tournaments/${tournament._id}`);
  };

  const handleDeleteTournament = (tournament: Tournament) => {
    setTournamentToDelete(tournament);
    setShowDeleteModal(true);
    setDeleteError(null);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setTournamentToDelete(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!tournamentToDelete) return;

    try {
      setIsDeleting(true);
      setDeleteError(null);
      
      // Call the delete API using the service
      await deleteTournament(tournamentToDelete._id);
      
      // Remove the tournament from the list
      setTournaments(prevTournaments => prevTournaments.filter(t => t._id !== tournamentToDelete._id));
      
      setShowDeleteModal(false);
      setTournamentToDelete(null);
    } catch (error: any) {
      console.error('Error deleting tournament:', error);
      setDeleteError(error.message || 'Failed to delete tournament. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Access denied for non-admin users
  if (!isAdminUser) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <h3>Access Denied</h3>
            <p>You don't have permission to access this page.</p>
            <Button variant="primary" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Tournaments', onClick: () => navigate('/tournaments') },
    { label: 'Tournament Team Registrations', active: true }
  ];

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>
        
        <Row>
          <Col>
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h4 className="card-title mb-0">Tournament Team Registrations</h4>
                </div>
              </Card.Header>
              
              <Card.Body>
                {loading ? (
                  <ShimmerLoader type="table" />
                ) : (
                  <>
                    {/* Search and Filters */}
                    <Row className="mb-3">
                      <Col md={3}>
                        <Form.Label className="form-label">Search</Form.Label>
                        <div className="search-input-wrapper">
                          <input
                            type="text"
                            className="form-search"
                            placeholder="Search tournament team registrations..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ width: '100%', paddingRight: '35px' }}
                          />
                          {searchTerm && (
                            <button
                              type="button"
                              className="clear-search-btn"
                              onClick={() => setSearchTerm('')}
                              title="Clear search"
                            >
                              <i className="ri-close-line"></i>
                            </button>
                          )}
                        </div>
                      </Col>
                      <Col md={3}>
                        <Form.Label className="form-label">Sports</Form.Label>
                        <Select
                          value={sportsOptions.find(option => option.value === sportsFilter)}
                          onChange={handleSportsFilterChange}
                          options={sportsOptions}
                          placeholder="Select Sports"
                          isClearable={false}
                        />
                      </Col>
                      <Col md={3}>
                        <Form.Label className="form-label">Venue</Form.Label>
                        <Select
                          value={venueOptions.find(option => option.value === venueFilter)}
                          onChange={handleVenueFilterChange}
                          options={venueOptions}
                          placeholder="Select Venue"
                          isClearable={false}
                        />
                      </Col>
                      <Col md={3}>
                        <Form.Label className="form-label">Age Category</Form.Label>
                        <Select
                          value={ageCategoryOptions.find(option => option.value === ageCategoryFilter)}
                          onChange={handleAgeCategoryFilterChange}
                          options={ageCategoryOptions}
                          placeholder="Select Age Category"
                          isClearable={false}
                        />
                      </Col>
                    </Row>

                    {/* Tournament Team Registrations Table */}
                    <TournamentTeamRegistrationsTable
                      tournaments={filteredTournaments}
                      loading={isPageLoading}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                      onPageChange={handlePageChange}
                      onViewTournament={handleViewTournament}
                      onEditTournament={handleEditTournament}
                      onDeleteTournament={handleDeleteTournament}
                    />
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Delete Confirmation Modal */}
        <DeleteModal
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          deleteModalFunction={confirmDelete}
          title="Delete Tournament"
          message={`Are you sure you want to delete "${tournamentToDelete?.name}"? This action cannot be undone.`}
          error={deleteError}
          isLoading={isDeleting}
          confirmLabel="Delete Tournament"
          cancelLabel="Cancel"
        />
      </Container>
    </div>
  );
};

export default RegisteredTournaments; 